import React, { useContext, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import OptimizeBuildingFilter from '@totem/components/services/optimize/v3/optimizeBuildingFilter';
import OptimizeLifecycleTab from '@totem/components/services/optimize/v3/optimizeLifecycleTab';
import OptimizeNeedsAttentionTab from '@totem/components/services/optimize/v3/optimizeNeedsAttentionTab';
import OptimizeOpportunitiesTab from '@totem/components/services/optimize/v3/optimizeOpportunitiesTab';
import OptimizeOverviewTab from '@totem/components/services/optimize/v3/optimizeOverviewTab';
import OptimizeProgramsTab from '@totem/components/services/optimize/v3/optimizeProgramsTab';
import TicketingListContainerFilterChips from '@totem/components/ticketing/listContainer/TicketingListContainerFilterChips';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';

import '../optimize.css';

const DEFAULT_TAB = 'overview';

const VALID_TABS = new Set([
  'overview',
  'opportunities',
  'lifecycle',
  'programs',
  'needsAttention',
]);

const OptimizeLayout3 = () => {
  const [searchParams] = useSearchParams();
  const { userProfile } = useContext(UserProfileContext);

  const initialTab = (() => {
    const param = searchParams.get('t');
    return param && VALID_TABS.has(param) ? param : DEFAULT_TAB;
  })();

  // Track which tab keys have ever been activated so we only mount a tab's
  // children (and its data fetches) when the user first visits that tab.
  const [activeKey, setActiveKey] = useState<string>(initialTab);
  const [activatedKeys, setActivatedKeys] = useState<Set<string>>(
    new Set([initialTab]),
  );

  const handleTabChange = (key: string) => {
    setActiveKey(key);

    setActivatedKeys((prev) => new Set([...prev, key]));
  };

  const getTabItems = () => {
    const tabItems = [
      {
        key: 'overview',
        label: 'Overview',
        children: activatedKeys.has('overview') ? (
          <OptimizeOverviewTab onSwitchTab={handleTabChange} />
        ) : null,
      },
      {
        key: 'opportunities',
        label: 'Opportunities',
        children: activatedKeys.has('opportunities') ? (
          <OptimizeOpportunitiesTab />
        ) : null,
      },
      {
        key: 'lifecycle',
        label: 'Lifecycle',
        children: activatedKeys.has('lifecycle') ? (
          <OptimizeLifecycleTab />
        ) : null,
      },
      {
        key: 'programs',
        label: 'Programs',
        children: activatedKeys.has('programs') ? (
          <OptimizeProgramsTab />
        ) : null,
      },
    ];

    if (permissions.internal.hasPermissions(userProfile)) {
      tabItems.push({
        key: 'needsAttention',
        label: 'Needs Attention',
        children: activatedKeys.has('needsAttention') ? (
          <OptimizeNeedsAttentionTab />
        ) : null,
      });
    }

    return tabItems;
  };

  return (
    <>
      {/* table-chips-container (optimize.css) carries a -2rem top margin to
          tuck the filter chips under a card header elsewhere — cancel that
          out (2rem), then add the 0.5rem gap we actually want here. */}
      <div style={{ marginBottom: '2.5rem' }}>
        <OptimizeBuildingFilter />
      </div>
      <div styleName="table-chips-container">
        <TicketingListContainerFilterChips />
      </div>
      <IBTabs
        activeKey={activeKey}
        items={getTabItems()}
        onChange={handleTabChange}
      />
    </>
  );
};

export default OptimizeLayout3;
